import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import {
  HelpOutline,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as WorkflowIcon,
  Category as CategoryIcon,
  AddCircle as AddCircleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Info as InfoIcon,
  PlayArrow as PlayIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";

const NewWorkflowUserGuide = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "90vh",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
          color: theme.palette.info.contrastText,
          fontWeight: 700,
          fontSize: "1.5rem",
          py: 3,
          px: 4,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.contrastText, 0.15),
              border: `2px solid ${alpha(theme.palette.info.contrastText, 0.3)}`,
            }}
          >
            <HelpOutline sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("newWorkFlow.userGuideTitle")}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              {t("newWorkFlow.userGuideSubtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: theme.palette.info.contrastText,
            "&:hover": {
              bgcolor: alpha(theme.palette.info.contrastText, 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          {/* Overview Section */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "info.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <WorkflowIcon /> {t("newWorkFlow.overviewTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("newWorkFlow.overviewText")}
            </Typography>
          </Box>

          {/* Section 1: What is a Workflow? */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <InfoIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("newWorkFlow.section1Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("newWorkFlow.section1Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section1Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section1Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section1Step3")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section1Step4")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Starting a New Workflow */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <PlayIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("newWorkFlow.section2Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("newWorkFlow.section2Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section2Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section2Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section2Step3")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <CategoryIcon
                      sx={{ color: "primary.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section2Step4")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <CategoryIcon
                      sx={{ color: "primary.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section2Step5")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Naming and Targeting */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SettingsIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("newWorkFlow.section3Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EditIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section3Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CategoryIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section3Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AssignmentIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section3Step3")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section3Step4")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section3Step5")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 4: Adding Steps */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AddCircleIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("newWorkFlow.section4Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("newWorkFlow.section4Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step3")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step4")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step5")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step6")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step7")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step8")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step9")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step10")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step11")} />
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step12")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="5" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section4Step13")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 5: Managing Steps */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <EditIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("newWorkFlow.section5Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EditIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section5Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section5Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section5Step3")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CategoryIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section5Step4")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WorkflowIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section5Step5")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 6: Saving the Workflow */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SaveIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("newWorkFlow.section6Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section6Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section6Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section6Step3")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section6Step4")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 7: Best Practices */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <StarIcon sx={{ color: "success.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("newWorkFlow.section8Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section8Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section8Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section8Step3")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section8Step4")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("newWorkFlow.section8Step5")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Quick Reference */}
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.05),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, color: "success.main" }}
            >
              {t("newWorkFlow.quickRefTitle")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="1" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("newWorkFlow.quickRef1Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("newWorkFlow.quickRef1Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="2" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("newWorkFlow.quickRef2Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("newWorkFlow.quickRef2Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="3" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("newWorkFlow.quickRef3Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("newWorkFlow.quickRef3Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="4" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("newWorkFlow.quickRef4Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("newWorkFlow.quickRef4Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="5" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("newWorkFlow.quickRef5Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("newWorkFlow.quickRef5Text")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
          }}
          aria-label={t("newWorkFlow.closeAria")}
        >
          {t("newWorkFlow.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewWorkflowUserGuide.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NewWorkflowUserGuide;
